import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Chip,
  Paper,
  IconButton,
  CircularProgress,
  Grid,
  Divider,
  Slider,
} from '@mui/material';
import {
  Close,
  MyLocation,
  Search,
  Place,
  Fullscreen,
  FullscreenExit,
} from '@mui/icons-material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { JobSearchService } from '../../services/jobSearchService';
import { supabase } from '../../lib/supabase';

// Fix for default markers in Leaflet with Webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Neuss coordinates
const NEUSS_COORDINATES = [51.1985, 6.6850] as [number, number];

// Create beautiful orange marker icon for Neuss
const createOrangeMarkerIcon = () => {
  return L.divIcon({
    className: 'neuss-orange-marker',
    html: `
      <div style="
        background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
        width: 30px;
        height: 30px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid #ffffff;
        box-shadow: 0 4px 12px rgba(255, 107, 53, 0.4), 0 0 0 4px rgba(255, 107, 53, 0.2);
        position: relative;
        animation: neussPulse 2s infinite;
      ">
        <div style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(45deg);
          color: white;
          font-weight: bold;
          font-size: 12px;
          text-shadow: 0 1px 2px rgba(0,0,0,0.3);
        ">N</div>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -30]
  });
};

// Create beautiful orange marker icon for addresses
const createOrangeAddressMarkerIcon = (index: number) => {
  return L.divIcon({
    className: 'orange-address-marker',
    html: `
      <div style="
        background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
        width: 32px;
        height: 32px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid #ffffff;
        box-shadow: 0 4px 12px rgba(255, 107, 53, 0.4), 0 0 0 4px rgba(255, 107, 53, 0.2);
        position: relative;
        animation: orangeAddressPulse 2s infinite;
      ">
        <div style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(45deg);
          color: white;
          font-weight: bold;
          font-size: 14px;
          text-shadow: 0 1px 2px rgba(0,0,0,0.3);
        ">🏠</div>
        <div style="
          position: absolute;
          top: -8px;
          right: -8px;
          background: #ffffff;
          color: #ff6b35;
          border-radius: 50%;
          width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: bold;
          border: 2px solid #ff6b35;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        ">${index + 1}</div>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};

interface DetailedLocation {
  name: string;
  lat: number;
  lng: number;
  country?: string;
  state?: string;
  city?: string;
  district?: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
}

interface MapSearchNewProps {
  open: boolean;
  onClose: () => void;
  onLocationSelect: (location: DetailedLocation) => void;
  selectedLocations: string[];
  initialSearchQuery?: string;
  isJobMode?: boolean; // Add prop to determine if we're in job mode
  // New props for address display mode
  addressMode?: boolean;
  addressData?: {
    title: string;
    full_name: string;
    company?: string;
    address_line_1: string;
    address_line_2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    phone?: string;
  };
  // Props for initial coordinates
  initialCoordinates?: {
    lat: number;
    lng: number;
  };
}

const MapSearchNew: React.FC<MapSearchNewProps> = ({
  open,
  onClose,
  onLocationSelect,
  selectedLocations,
  initialSearchQuery,
  isJobMode = false, // Default to false (shop mode)
  addressMode = false,
  addressData,
  initialCoordinates,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const circleRef = useRef<L.Circle | null>(null);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<DetailedLocation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [radiusKm, setRadiusKm] = useState(10); // Radius in kilometers
  const [jobsInRadius, setJobsInRadius] = useState<number | null>(null);
  const [searchingJobs, setSearchingJobs] = useState(false);
  const [showRadius, setShowRadius] = useState(true); // Control radius visibility
  const [allJobs, setAllJobs] = useState<any[]>([]);
  const [jobMarkers, setJobMarkers] = useState<L.Marker[]>([]);
  const [fetchingJobs, setFetchingJobs] = useState(false);
  const [jobCount, setJobCount] = useState<number | null>(null);
  const [isMaximized, setIsMaximized] = useState(false);
  
  // Address search states
  const [searchingAddresses, setSearchingAddresses] = useState(false);
  const [addressSearchResults, setAddressSearchResults] = useState<number | null>(null);
  const [foundAddresses, setFoundAddresses] = useState<any[]>([]);
  const [addressMarkers, setAddressMarkers] = useState<L.Marker[]>([]);
  
  // Manual coordinate input states
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');
  const [manualLocationName, setManualLocationName] = useState('');
  const [addingManualMarker, setAddingManualMarker] = useState(false);
  const [manualMarkers, setManualMarkers] = useState<L.Marker[]>([]);
  
  // Location error states
  const [locationError, setLocationError] = useState<string | null>(null);
  const [geocodingFailed, setGeocodingFailed] = useState(false);
  
  // Marker address state for Test section
  const [markerAddress, setMarkerAddress] = useState<{
    coordinates: { lat: number; lng: number } | null;
    address: string | null;
    timestamp: string | null;
    // Structured address components
    street?: string;
    houseNumber?: string;
    city?: string;
    state?: string;
    country?: string;
    postalCode?: string;
    district?: string;
  }>({
    coordinates: null,
    address: null,
    timestamp: null,
  });

  // Function to parse address string into structured components
  const parseAddressString = (addressString: string) => {
    console.log('🔍 Parsing address string:', addressString);
    const parts = addressString.split(',').map(part => part.trim());
    console.log('🔍 Address parts:', parts);
    
    // Try to extract structured components
    let street = '';
    let houseNumber = '';
    let city = '';
    let state = '';
    let country = '';
    let postalCode = '';
    let district = '';
    
    if (parts.length > 0) {
      // First part is usually street address
      const streetPart = parts[0];
      console.log('🔍 Street part:', streetPart);
      
      // Try to extract house number and street - look for number at the beginning
      // Use a more specific regex that requires at least one space after the number
      const streetMatch = streetPart.match(/^(\d+)\s+([^\d].+)$/);
      if (streetMatch) {
        houseNumber = streetMatch[1];
        street = streetMatch[2].trim();
        console.log('🔍 Extracted - House Number:', houseNumber, 'Street:', street);
      } else {
        // If no number at start, treat entire first part as street
        street = streetPart;
        console.log('🔍 No number found, using entire street part:', street);
      }
    }
    
    if (parts.length > 1) {
      city = parts[1];
    }
    
    if (parts.length > 2) {
      state = parts[2];
    }
    
    if (parts.length > 3) {
      country = parts[parts.length - 1]; // Usually the last part
    }
    
    // Try to find postal code (usually 5 digits)
    const postalCodeMatch = addressString.match(/\b\d{5}\b/);
    if (postalCodeMatch) {
      postalCode = postalCodeMatch[0];
    }
    
    const result = {
      street,
      houseNumber,
      city,
      state,
      country,
      postalCode,
      district
    };
    
    console.log('🔍 Parsed address result:', result);
    return result;
  };

  // Debug: Track showRadius state changes
  useEffect(() => {
    console.log('🔍 showRadius state changed to:', showRadius);
    console.log('🔍 selectedLocation:', selectedLocation);
    console.log('🔍 Should show radius section:', selectedLocation && showRadius);
  }, [showRadius, selectedLocation]);

  // Debug: Track map loading and job markers
  useEffect(() => {
    console.log('🗺️ Map loaded state changed to:', mapLoaded);
    console.log('🗺️ All jobs available:', allJobs.length);
    console.log('🗺️ Map instance available:', !!mapInstanceRef.current);
    
    // If map is loaded and we have jobs, try to display markers
    if (mapLoaded && allJobs.length > 0 && mapInstanceRef.current) {
      console.log('🔄 Map loaded with jobs - attempting to display markers...');
      setTimeout(() => {
        displayJobMarkersOnMap(allJobs);
      }, 1000);
    }
  }, [mapLoaded, allJobs.length]);

  // Initialize map when modal opens
  useLayoutEffect(() => {
    console.log('MapSearchNew useLayoutEffect triggered, open:', open);
    
    if (!open) {
      // Clean up when modal is closed
      console.log('Modal closed, cleaning up map...');
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        setMapLoaded(false);
        setMapError(null);
      }
      // Clean up refs
      markerRef.current = null;
      circleRef.current = null;
      
      // Clean up manual markers
      setManualMarkers([]);
      
      // Clean up job markers
      jobMarkers.forEach(marker => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.removeLayer(marker);
        }
      });
      setJobMarkers([]);
      setAllJobs([]);
      setJobCount(null);
      
      // Clean up address markers
      addressMarkers.forEach(marker => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.removeLayer(marker);
        }
      });
      setAddressMarkers([]);
      setFoundAddresses([]);
      setAddressSearchResults(null);
      
      // Clean up error states
      setGeocodingFailed(false);
      setLocationError(null);
      
      // Clean up marker address
      setMarkerAddress({
        coordinates: null,
        address: null,
        timestamp: null,
      });
      
      return;
    }

    // Initialize map when modal opens
    const initializeMap = () => {
      console.log('Attempting to initialize map...');
      console.log('mapRef.current:', mapRef.current);
      console.log('mapInstanceRef.current:', mapInstanceRef.current);
      
      if (!mapRef.current) {
        console.warn('Map ref not available');
        return;
      }

      if (mapInstanceRef.current) {
        console.log('Map already initialized');
        return;
      }

      try {
        console.log('Creating Leaflet map...');
        setMapError(null);
        
        const map = L.map(mapRef.current, {
          center: initialCoordinates ? [initialCoordinates.lat, initialCoordinates.lng] : [40.7128, -74.0060],
          zoom: initialCoordinates ? 15 : 10,
          zoomControl: true,
        });
        
        console.log('Adding tile layer...');
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors',
          maxZoom: 19,
        }).addTo(map);

        mapInstanceRef.current = map;
        setMapLoaded(true);
        console.log('Map initialized successfully!');

        // Add beautiful orange marker for Neuss
        const neussMarker = L.marker(NEUSS_COORDINATES, { 
          icon: createOrangeMarkerIcon(),
          riseOnHover: true
        }).addTo(map);
        
        // Add popup with welcome message for Neuss
        neussMarker.bindPopup(`
          <div style="padding: 16px; min-width: 280px; font-family: Arial, sans-serif; text-align: center;">
            <div style="
              background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
              color: white;
              padding: 12px;
              border-radius: 8px;
              margin-bottom: 12px;
              font-weight: bold;
              font-size: 18px;
              text-shadow: 0 1px 2px rgba(0,0,0,0.3);
            ">
              🧡 Welcome to Neuss! 🧡
            </div>
            <p style="margin: 8px 0; color: #333; font-size: 14px;">
              <strong>Neuss, Germany</strong><br>
              A beautiful city in North Rhine-Westphalia
            </p>
            <p style="margin: 8px 0; color: #666; font-size: 12px;">
              Coordinates: 51.1985°N, 6.6850°E
            </p>
            <div style="
              background: #f8f9fa;
              padding: 8px;
              border-radius: 4px;
              margin-top: 8px;
              font-size: 11px;
              color: #666;
            ">
              This is your special orange marker! 🎯
            </div>
          </div>
        `);

        // Add click handler
        map.on('click', (e) => {
          const { lat, lng } = e.latlng;
          handleMapClick(lat, lng);
        });

        // Force map to resize and invalidate size after initialization
        setTimeout(() => {
          if (mapInstanceRef.current) {
            console.log('🔄 Forcing map resize after initialization...');
            mapInstanceRef.current.invalidateSize();
            mapInstanceRef.current.fitBounds(mapInstanceRef.current.getBounds());
          }
        }, 100);

        // Fetch and display all jobs with coordinates
        fetchAndDisplayAllJobs();
      } catch (error) {
        console.error('Error initializing map:', error);
        setMapError('Failed to load map. Please try again.');
        setMapLoaded(true); // Still set as loaded to hide spinner
      }
    };

    // Try to initialize immediately
    initializeMap();

    // If that didn't work, try again after a short delay
    const timeoutId = setTimeout(() => {
      if (!mapInstanceRef.current) {
        console.log('Retrying map initialization after timeout...');
        initializeMap();
      }
    }, 100);

    // Fallback timeout to prevent endless loading
    const fallbackTimeout = setTimeout(() => {
      if (!mapInstanceRef.current) {
        console.warn('Map failed to load within timeout, showing error');
        setMapError('Failed to load map. Please check your internet connection.');
        setMapLoaded(true);
      }
    }, 5000);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(fallbackTimeout);
    };
  }, [open]);

  // Component mount effect
  useEffect(() => {
    console.log('MapSearchNew component mounted, open:', open);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log('MapSearchNew component unmounting, cleaning up map...');
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  const retryMapInitialization = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }
    setMapLoaded(false);
    setMapError(null);
    
    // Trigger re-initialization
    setTimeout(() => {
      if (mapRef.current && !mapInstanceRef.current) {
        try {
          console.log('Retrying map initialization...');
          setMapError(null);
          
          const map = L.map(mapRef.current, {
            center: initialCoordinates ? [initialCoordinates.lat, initialCoordinates.lng] : [40.7128, -74.0060],
            zoom: initialCoordinates ? 15 : 10,
            zoomControl: true,
          });
          
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors',
            maxZoom: 19,
          }).addTo(map);

          mapInstanceRef.current = map;
          setMapLoaded(true);
          console.log('Map retry successful');

          // Add beautiful orange marker for Neuss
          const neussMarker = L.marker(NEUSS_COORDINATES, { 
            icon: createOrangeMarkerIcon(),
            riseOnHover: true
          }).addTo(map);
          
          // Add popup with welcome message for Neuss
          neussMarker.bindPopup(`
            <div style="padding: 16px; min-width: 280px; font-family: Arial, sans-serif; text-align: center;">
              <div style="
                background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
                color: white;
                padding: 12px;
                border-radius: 8px;
                margin-bottom: 12px;
                font-weight: bold;
                font-size: 18px;
                text-shadow: 0 1px 2px rgba(0,0,0,0.3);
              ">
                🧡 Welcome to Neuss! 🧡
              </div>
              <p style="margin: 8px 0; color: #333; font-size: 14px;">
                <strong>Neuss, Germany</strong><br>
                A beautiful city in North Rhine-Westphalia
              </p>
              <p style="margin: 8px 0; color: #666; font-size: 12px;">
                Coordinates: 51.1985°N, 6.6850°E
              </p>
              <div style="
                background: #f8f9fa;
                padding: 8px;
                border-radius: 4px;
                margin-top: 8px;
                font-size: 11px;
                color: #666;
              ">
                This is your special orange marker! 🎯
              </div>
            </div>
          `);

          // Add click handler
          map.on('click', (e) => {
            const { lat, lng } = e.latlng;
            handleMapClick(lat, lng);
          });

          // Fetch and display all jobs with coordinates
          fetchAndDisplayAllJobs();
        } catch (error) {
          console.error('Error retrying map initialization:', error);
          setMapError('Failed to load map. Please check your internet connection.');
          setMapLoaded(true);
        }
      }
    }, 100);
  };

  const handleMapClick = async (lat: number, lng: number) => {
    console.log('🗺️ Map clicked at:', { lat, lng });
    
    if (markerRef.current) {
      markerRef.current.remove();
    }

    // Add new marker
    const marker = L.marker([lat, lng]).addTo(mapInstanceRef.current!);
    markerRef.current = marker;

    // Get location name using reverse geocoding
    try {
      setIsLoading(true);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
      );
      const data = await response.json();
      
      const locationName = data.display_name || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
      const address = data.address || {};
      
      const location: DetailedLocation = {
        name: locationName,
        lat,
        lng,
        country: address.country,
        state: address.state,
        city: address.city || address.town || address.village,
        district: address.suburb || address.neighbourhood,
        street: address.road,
        houseNumber: address.house_number,
        postalCode: address.postcode,
      };
      
      setSelectedLocation(location);
      
      // Update marker address for Test section
      const parsedAddress = parseAddressString(locationName);
      setMarkerAddress({
        coordinates: { lat, lng },
        address: locationName,
        timestamp: new Date().toLocaleString(),
        ...parsedAddress
      });
      
      // Reset job count when location changes
      setJobsInRadius(null);
      
      // Show radius when location is selected (not in address mode)
      console.log('🔄 Setting showRadius to true after map click');
      setShowRadius(!addressMode);
    } catch (error) {
      console.error('Error getting location name:', error);
      const fallbackName = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
      setSelectedLocation({ 
        name: fallbackName, 
        lat, 
        lng 
      });
      
      // Update marker address for Test section (error case)
      const parsedAddress = parseAddressString(fallbackName);
      setMarkerAddress({
        coordinates: { lat, lng },
        address: `Coordinates: ${fallbackName}`,
        timestamp: new Date().toLocaleString(),
        ...parsedAddress
      });
      
      // Show radius when location is selected (even in error case, not in address mode)
      console.log('🔄 Setting showRadius to true after map click (error case)');
      setShowRadius(!addressMode);
    } finally {
      setIsLoading(false);
    }
    
    // Update radius circle
    updateRadiusCircle(lat, lng);
  };

  // Function to fetch just the job count
  const fetchJobCount = async () => {
    // Only fetch job count if we're in job mode
    if (!isJobMode) {
      console.log('🚫 Skipping job count fetch - not in job mode');
      return;
    }

    setFetchingJobs(true);
    setJobCount(null);
    
    try {
      console.log('🔍 Fetching job count from products table (jobs only)...');
      
      const { data: jobs, error } = await supabase
        .from('products')
        .select('id, name, latitude, longitude, location_name')
        .eq('is_job', true)
        .not('latitude', 'is', null)
        .not('longitude', 'is', null);

      if (error) {
        console.error('❌ Error fetching job count:', error);
        setJobCount(0);
        return;
      }

      const count = jobs?.length || 0;
      setJobCount(count);
      
      console.log('✅ Job count result:', {
        count,
        jobs: jobs?.map(j => ({
          id: j.id,
          location: j.location_name,
          coordinates: `${j.latitude}, ${j.longitude}`
        }))
      });
      
      // Add red markers for all fetched jobs
      if (jobs && jobs.length > 0) {
        setTimeout(() => {
          console.log('🔴 Adding red markers for fetched jobs...');
          addRedMarkersForJobs(jobs);
        }, 500);
      }
      
    } catch (error) {
      console.error('❌ Error in fetchJobCount:', error);
      setJobCount(0);
    } finally {
      setFetchingJobs(false);
    }
  };

  // Function to fetch and display all jobs with coordinates
  const fetchAndDisplayAllJobs = async () => {
    // Only fetch jobs if we're in job mode
    if (!isJobMode) {
      console.log('🚫 Skipping job fetch - not in job mode');
      return;
    }

    try {
      console.log('🔍 Fetching all jobs with coordinates from products table (jobs only)...');
      
      const { data: jobs, error } = await supabase
        .from('products')
        .select('id, name, latitude, longitude, location_name')
        .eq('is_job', true)
        .not('latitude', 'is', null)
        .not('longitude', 'is', null);

      if (error) {
        console.error('❌ Error fetching jobs:', error);
        return;
      }

      console.log('✅ Found jobs with coordinates:', jobs?.length || 0, jobs);

      if (jobs && jobs.length > 0) {
        setAllJobs(jobs);
        
        // Add a small delay to ensure map is fully ready
        setTimeout(() => {
          console.log('🕐 Delayed marker display - map ready:', !!mapInstanceRef.current);
        displayJobMarkersOnMap(jobs);
        }, 500);
      }
    } catch (error) {
      console.error('❌ Error in fetchAndDisplayAllJobs:', error);
    }
  };

  // Function to search for addresses in radius
  const searchAddressesInRadius = async () => {
    if (!selectedLocation) {
      console.log('❌ No location selected for address search');
      return;
    }

    setSearchingAddresses(true);
    setAddressSearchResults(null);
    
    // Clear existing address markers
    addressMarkers.forEach(marker => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(marker);
      }
    });
    setAddressMarkers([]);
    setFoundAddresses([]);
    
    try {
      console.log('🔍 Searching for addresses in radius:', {
        center: { lat: selectedLocation.lat, lng: selectedLocation.lng },
        radiusKm,
        timestamp: new Date().toISOString()
      });
      
      // Query the addresses table for entries with coordinates within the radius
      const { data: addresses, error } = await supabase
        .from('addresses')
        .select('id, latitude, longitude, address_line_1, city, country')
        .not('latitude', 'is', null)
        .not('longitude', 'is', null);

      if (error) {
        console.error('❌ Error fetching addresses:', error);
        setAddressSearchResults(0);
        return;
      }

      console.log('✅ Found addresses with coordinates:', addresses?.length || 0);

      if (!addresses || addresses.length === 0) {
        setAddressSearchResults(0);
        return;
      }

      // Filter addresses within the radius
      const addressesInRadius = addresses.filter(address => {
        if (!address.latitude || !address.longitude) return false;
        
        // Calculate distance using Haversine formula
        const R = 6371; // Earth's radius in kilometers
        const dLat = (address.latitude - selectedLocation.lat) * Math.PI / 180;
        const dLng = (address.longitude - selectedLocation.lng) * Math.PI / 180;
        const a = 
          Math.sin(dLat/2) * Math.sin(dLat/2) +
          Math.cos(selectedLocation.lat * Math.PI / 180) * Math.cos(address.latitude * Math.PI / 180) * 
          Math.sin(dLng/2) * Math.sin(dLng/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        const distance = R * c; // Distance in kilometers
        
        return distance <= radiusKm;
      });

      const count = addressesInRadius.length;
      setAddressSearchResults(count);
      setFoundAddresses(addressesInRadius);
      
      // Display address markers on the map
      if (addressesInRadius.length > 0) {
        console.log('🏠 Scheduling marker display for found addresses...');
        // Wait for map to be ready, then display markers
        const displayMarkers = () => {
          if (mapInstanceRef.current && mapLoaded) {
            console.log('🏠 Map is ready, displaying address markers...');
            displayAddressMarkersOnMap(addressesInRadius);
          } else {
            console.log('🏠 Map not ready yet, retrying in 500ms...');
            setTimeout(displayMarkers, 500);
          }
        };
        setTimeout(displayMarkers, 500);
      }
      
      console.log('✅ Address search completed:', {
        totalAddresses: addresses.length,
        addressesInRadius: count,
        radiusKm,
        center: { lat: selectedLocation.lat, lng: selectedLocation.lng }
      });
      
    } catch (error) {
      console.error('❌ Error in searchAddressesInRadius:', error);
      setAddressSearchResults(0);
    } finally {
      setSearchingAddresses(false);
    }
  };

  // Function to display address markers on the map
  const displayAddressMarkersOnMap = (addresses: any[]) => {
    const mapContainer = mapInstanceRef.current?.getContainer();
    console.log('🏠 displayAddressMarkersOnMap called with:', {
      addressesCount: addresses?.length || 0,
      addresses: addresses,
      mapInstance: !!mapInstanceRef.current,
      mapLoaded: mapLoaded,
      mapReady: (mapContainer?.clientWidth || 0) > 0
    });

    if (!mapInstanceRef.current) {
      console.error('❌ Map instance not available for address marker display');
      return;
    }

    // Check if map is actually ready
    if (!mapContainer || mapContainer.clientWidth === 0) {
      console.error('❌ Map container not ready, retrying in 1 second...');
      setTimeout(() => {
        displayAddressMarkersOnMap(addresses);
      }, 1000);
      return;
    }

    // Remove existing address markers
    console.log('🗑️ Removing existing address markers:', addressMarkers.length);
    addressMarkers.forEach(marker => {
      try {
        mapInstanceRef.current?.removeLayer(marker);
      } catch (error) {
        console.warn('⚠️ Error removing marker:', error);
      }
    });

    const newMarkers: L.Marker[] = [];

    addresses.forEach((address, index) => {
      console.log(`🔍 Processing address ${index + 1}:`, {
        id: address.id,
        latitude: address.latitude,
        longitude: address.longitude,
        address_line_1: address.address_line_1,
        city: address.city,
        hasCoordinates: !!(address.latitude && address.longitude)
      });

      if (address.latitude && address.longitude) {
        try {
          // Create beautiful orange marker for address
          const addressIcon = createOrangeAddressMarkerIcon(index);

          console.log(`📍 Creating beautiful orange marker for address ${index + 1} at:`, [address.latitude, address.longitude]);

          const marker = L.marker([address.latitude, address.longitude], { 
            icon: addressIcon,
            riseOnHover: true
          }).addTo(mapInstanceRef.current!);

          // Add beautiful popup with address information
          marker.bindPopup(`
            <div style="padding: 16px; min-width: 300px; font-family: Arial, sans-serif; text-align: center;">
              <div style="
                background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
                color: white;
                padding: 12px;
                border-radius: 8px;
                margin-bottom: 12px;
                font-weight: bold;
                font-size: 16px;
                text-shadow: 0 1px 2px rgba(0,0,0,0.3);
              ">
                🧡 Address Found #${index + 1} 🧡
              </div>
              <div style="text-align: left; margin: 8px 0;">
                <p style="margin: 6px 0; color: #333; font-size: 14px;"><strong>🏠 Address:</strong> ${address.address_line_1 || 'N/A'}</p>
                <p style="margin: 6px 0; color: #333; font-size: 14px;"><strong>🏙️ City:</strong> ${address.city || 'N/A'}</p>
                <p style="margin: 6px 0; color: #333; font-size: 14px;"><strong>🌍 Country:</strong> ${address.country || 'N/A'}</p>
                <p style="margin: 6px 0; color: #666; font-size: 12px;"><strong>📍 Coordinates:</strong> ${address.latitude.toFixed(6)}, ${address.longitude.toFixed(6)}</p>
                <p style="margin: 6px 0; color: #666; font-size: 11px;"><strong>🆔 ID:</strong> ${address.id.substring(0, 8)}...</p>
              </div>
              <div style="
                background: #f8f9fa;
                padding: 8px;
                border-radius: 4px;
                margin-top: 8px;
                font-size: 11px;
                color: #666;
              ">
                Beautiful orange marker! 🎯
              </div>
            </div>
          `);

          newMarkers.push(marker);
          console.log(`✅ Address marker ${index + 1} created and added to map at [${address.latitude}, ${address.longitude}]`);
        } catch (error) {
          console.error(`❌ Error creating marker for address ${index + 1}:`, error);
        }
      } else {
        console.log(`❌ Address ${index + 1} missing coordinates:`, {
          id: address.id,
          latitude: address.latitude,
          longitude: address.longitude
        });
      }
    });

    setAddressMarkers(newMarkers);
    console.log(`🏠 Total address markers added to map: ${newMarkers.length}`);
    
    // Force map to refresh/redraw (but don't change zoom/position)
    if (mapInstanceRef.current) {
      mapInstanceRef.current.invalidateSize();
      console.log('🗺️ Map refreshed, markers placed without changing view');
    }
  };

  // Function to display job markers on the map
  const displayJobMarkersOnMap = (jobs: any[]) => {
    console.log('🎯 displayJobMarkersOnMap called with:', {
      jobsCount: jobs?.length || 0,
      jobs: jobs,
      mapInstance: !!mapInstanceRef.current,
      mapLoaded: mapLoaded
    });

    if (!mapInstanceRef.current) {
      console.error('❌ Map instance not available for marker display');
      return;
    }

    // Remove existing job markers
    console.log('🗑️ Removing existing markers:', jobMarkers.length);
    jobMarkers.forEach(marker => {
      mapInstanceRef.current?.removeLayer(marker);
    });

    const newMarkers: L.Marker[] = [];

    jobs.forEach((job, index) => {
      console.log(`🔍 Processing job ${index + 1}:`, {
        id: job.id,
        latitude: job.latitude,
        longitude: job.longitude,
        location_name: job.location_name,
        hasCoordinates: !!(job.latitude && job.longitude)
      });

      if (job.latitude && job.longitude) {
        // Create a custom icon for job markers
        const jobIcon = L.divIcon({
          className: 'job-marker',
          html: `
            <div style="
              background: #ff6b35;
              width: 25px;
              height: 25px;
              border-radius: 50%;
              border: 3px solid white;
              box-shadow: 0 2px 6px rgba(0,0,0,0.3);
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 14px;
              color: white;
              font-weight: bold;
              z-index: 1000;
            ">💼</div>
          `,
          iconSize: [25, 25],
          iconAnchor: [12, 12]
        });

        console.log(`📍 Creating marker for job ${index + 1} at:`, [job.latitude, job.longitude]);

        const marker = L.marker([job.latitude, job.longitude], { icon: jobIcon })
          .addTo(mapInstanceRef.current!);

        // Add popup with job information
        marker.bindPopup(`
          <div style="padding: 8px; min-width: 200px;">
            <h4 style="margin: 0 0 8px 0; color: #333;">💼 Company Job</h4>
            <p style="margin: 4px 0; color: #666;"><strong>ID:</strong> ${job.id.substring(0, 8)}...</p>
            <p style="margin: 4px 0; color: #666;"><strong>Location:</strong> ${job.location_name || 'Unknown'}</p>
            <p style="margin: 4px 0; color: #666;"><strong>Coordinates:</strong> ${job.latitude.toFixed(6)}, ${job.longitude.toFixed(6)}</p>
          </div>
        `);

        newMarkers.push(marker);
        console.log(`✅ Marker ${index + 1} created and added to map`);
      } else {
        console.log(`❌ Job ${index + 1} missing coordinates:`, {
          id: job.id,
          latitude: job.latitude,
          longitude: job.longitude
        });
      }
    });

    setJobMarkers(newMarkers);
    console.log(`🎯 Total markers added to map: ${newMarkers.length}`);
    
    // Force map to refresh/redraw
    if (mapInstanceRef.current) {
      mapInstanceRef.current.invalidateSize();
    }
  };

  // Function to add red markers for all fetched jobs
  const addRedMarkersForJobs = (jobs: any[]) => {
    if (!mapInstanceRef.current) {
      console.log('❌ Map not ready for job markers');
      return;
    }

    console.log('🔴 Adding red markers for jobs:', jobs.length);
    
    // Remove existing job markers first
    jobMarkers.forEach(marker => {
      mapInstanceRef.current?.removeLayer(marker);
    });
    
    const newJobMarkers: L.Marker[] = [];
    
    jobs.forEach((job, index) => {
      if (job.latitude && job.longitude) {
        console.log(`🔴 Creating red marker for job ${index + 1}:`, {
          id: job.id,
          name: job.name,
          location: job.location_name,
          coordinates: [job.latitude, job.longitude]
        });
        
        // Create a beautiful job marker with card
        const jobIcon = L.divIcon({
          className: 'job-marker-with-card',
          html: `
            <div style="
              position: relative;
              display: flex;
              flex-direction: column;
              align-items: center;
            ">
              <!-- Beautiful Job Card -->
              <div style="
                background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
                border: 2px solid #f44336;
                border-radius: 12px;
                padding: 8px 12px;
                margin-bottom: 5px;
                box-shadow: 0 4px 15px rgba(244, 67, 54, 0.2);
                min-width: 120px;
                text-align: center;
                position: relative;
                z-index: 1000;
              ">
                <!-- Card Header -->
                <div style="
                  background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%);
                  color: white;
                  padding: 4px 8px;
                  border-radius: 8px;
                  margin-bottom: 6px;
                  font-size: 12px;
                  font-weight: bold;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                ">
                  💼 Job
                </div>
                
                <!-- Job Name -->
                <div style="
                  font-size: 13px;
                  font-weight: 600;
                  color: #333;
                  margin-bottom: 4px;
                  line-height: 1.2;
                  word-wrap: break-word;
                  max-width: 150px;
                ">
                  ${job.name || 'Unnamed Job'}
                </div>
                
                <!-- Location -->
                <div style="
                  font-size: 11px;
                  color: #666;
                  margin-bottom: 2px;
                  line-height: 1.1;
                ">
                  📍 ${job.location_name || 'Unknown Location'}
                </div>
                
                <!-- Coordinates -->
                <div style="
                  font-size: 10px;
                  color: #888;
                  font-family: monospace;
                  background: #f5f5f5;
                  padding: 2px 4px;
                  border-radius: 4px;
                  margin-top: 4px;
                ">
                  ${job.latitude.toFixed(4)}, ${job.longitude.toFixed(4)}
                </div>
              </div>
              
              <!-- Red Marker Pin -->
              <div style="
                background: #f44336;
                width: 20px;
                height: 20px;
                border-radius: 50% 50% 50% 0;
                transform: rotate(-45deg);
                border: 2px solid white;
                box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                display: flex;
                align-items: center;
                justify-content: center;
                position: relative;
                z-index: 999;
              ">
                <div style="
                  transform: rotate(45deg);
                  color: white;
                  font-size: 12px;
                  font-weight: bold;
                ">💼</div>
              </div>
            </div>
          `,
          iconSize: [200, 100],
          iconAnchor: [100, 100]
        });

        const marker = L.marker([job.latitude, job.longitude], { icon: jobIcon })
          .addTo(mapInstanceRef.current!);

        newJobMarkers.push(marker);
        console.log(`✅ Red marker ${index + 1} created and added to map`);
      } else {
        console.log(`❌ Job ${index + 1} missing coordinates:`, {
          id: job.id,
          latitude: job.latitude,
          longitude: job.longitude
        });
      }
    });

    setJobMarkers(newJobMarkers);
    console.log(`🔴 Total red job markers added: ${newJobMarkers.length}`);
    
    // Force map refresh
    if (mapInstanceRef.current) {
      mapInstanceRef.current.invalidateSize();
    }
  };

  // Function to handle job label click - fill manual marker form AND add marker
  const handleJobLabelClick = async (job: any) => {
    console.log('🖱️ Job label clicked:', job);
    
    if (job.latitude && job.longitude) {
      // Fill the manual marker form with job coordinates
      setManualLat(job.latitude.toString());
      setManualLng(job.longitude.toString());
      setManualLocationName(job.name || job.location_name || 'Job Location');
      
      console.log('✅ Manual marker form filled with:', {
        lat: job.latitude,
        lng: job.longitude,
        name: job.name || job.location_name
      });
      
      // Automatically add the marker to the map
      try {
        console.log('🚀 Automatically adding marker to map...');
        console.log('🚀 Map instance available:', !!mapInstanceRef.current);
        console.log('🚀 Map loaded:', mapLoaded);
        console.log('🚀 Job coordinates:', { lat: job.latitude, lng: job.longitude });
        
        if (!mapInstanceRef.current) {
          console.error('❌ Map instance not available');
          alert('Map not ready. Please try again.');
          return;
        }
        
        if (!mapLoaded) {
          console.error('❌ Map not loaded yet');
          alert('Map is still loading. Please wait and try again.');
          return;
        }

        // Create a custom icon for job markers (same as manual markers)
        const jobIcon = L.divIcon({
          className: 'job-marker',
          html: `
            <div style="
              position: relative;
              width: 30px;
              height: 30px;
            ">
              <div style="
                background: #ff6b35;
                width: 30px;
                height: 30px;
                border-radius: 50% 50% 50% 0;
                transform: rotate(-45deg);
                border: 3px solid white;
                box-shadow: 0 2px 10px rgba(0,0,0,0.3);
                display: flex;
                align-items: center;
                justify-content: center;
                position: relative;
              ">
                <div style="
                  transform: rotate(45deg);
                  color: white;
                  font-size: 16px;
                  font-weight: bold;
                  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
                ">💼</div>
              </div>
            </div>
          `,
          iconSize: [30, 30],
          iconAnchor: [15, 30]
        });

        const marker = L.marker([job.latitude, job.longitude], { icon: jobIcon })
          .addTo(mapInstanceRef.current);

        console.log('✅ Marker created and added to map:', marker);
        console.log('✅ Marker position:', marker.getLatLng());

        // Add popup with job information
        marker.bindPopup(`
          <div style="padding: 8px; min-width: 200px;">
            <h4 style="margin: 0 0 8px 0; color: #333;">💼 ${job.name || 'Job'}</h4>
            <p style="margin: 4px 0; color: #666;"><strong>Location:</strong> ${job.location_name || 'Unknown'}</p>
            <p style="margin: 4px 0; color: #666;"><strong>Coordinates:</strong> ${job.latitude.toFixed(6)}, ${job.longitude.toFixed(6)}</p>
            <p style="margin: 4px 0; color: #666;"><strong>Added:</strong> ${new Date().toLocaleString()}</p>
          </div>
        `);

        // Add to manual markers array
        setManualMarkers(prev => {
          console.log('📝 Adding marker to manual markers array. Previous count:', prev.length);
          const newArray = [...prev, marker];
          console.log('📝 New manual markers count:', newArray.length);
          return newArray;
        });
        
        // Center map on the new marker
        console.log('🎯 Centering map on marker at:', [job.latitude, job.longitude]);
        mapInstanceRef.current.setView([job.latitude, job.longitude], 13);
        
        // Force map refresh
        mapInstanceRef.current.invalidateSize();
        
        console.log('✅ Job marker added successfully to map!');
        
        // Show success message
        alert(`✅ Job marker added!\n\nJob: ${job.name || job.location_name}\nCoordinates: ${job.latitude}, ${job.longitude}\n\nMarker is now on the map!`);
        
      } catch (error) {
        console.error('❌ Error adding job marker:', error);
        console.log('🔄 Falling back to manual marker addition...');
        
        // Fallback: Use the working addManualMarker function
        try {
          // Set the form values first
          setManualLat(job.latitude.toString());
          setManualLng(job.longitude.toString());
          setManualLocationName(job.name || job.location_name || 'Job Location');
          
          // Wait a bit for state to update, then call addManualMarker
          setTimeout(() => {
            console.log('🔄 Calling addManualMarker as fallback...');
            addManualMarker();
          }, 100);
          
        } catch (fallbackError) {
          console.error('❌ Fallback also failed:', fallbackError);
          alert('Error adding marker to map. Please try the manual "Add Marker" button.');
        }
      }
      
    } else {
      console.log('❌ Job has no coordinates');
      alert('This job has no coordinates available');
    }
  };

  // Function to add a manual marker from coordinates
  const addManualMarker = async () => {
    if (!mapInstanceRef.current) {
      console.error('❌ Map instance not available');
      return;
    }

    // Validate coordinates
    const lat = parseFloat(manualLat);
    const lng = parseFloat(manualLng);
    
    if (isNaN(lat) || isNaN(lng)) {
      alert('Please enter valid latitude and longitude numbers');
      return;
    }
    
    if (lat < -90 || lat > 90) {
      alert('Latitude must be between -90 and 90 degrees');
      return;
    }
    
    if (lng < -180 || lng > 180) {
      alert('Longitude must be between -180 and 180 degrees');
      return;
    }

    setAddingManualMarker(true);
    
    try {
      console.log('📍 Adding manual marker at:', { lat, lng, name: manualLocationName });
      
      // Create a custom icon for manual markers
      const manualIcon = L.divIcon({
        className: 'manual-marker',
        html: `
          <div style="
            position: relative;
            width: 30px;
            height: 30px;
          ">
            <div style="
              background: #ff6b35;
              width: 30px;
              height: 30px;
              border-radius: 50% 50% 50% 0;
              transform: rotate(-45deg);
              border: 3px solid white;
              box-shadow: 0 2px 10px rgba(0,0,0,0.3);
              display: flex;
              align-items: center;
              justify-content: center;
              position: relative;
            ">
              <div style="
                transform: rotate(45deg);
                color: white;
                font-size: 16px;
                font-weight: bold;
                text-shadow: 0 1px 2px rgba(0,0,0,0.3);
              ">📍</div>
            </div>
          </div>
        `,
        iconSize: [30, 30],
        iconAnchor: [15, 30]
      });

      const marker = L.marker([lat, lng], { icon: manualIcon })
        .addTo(mapInstanceRef.current);

      // Add popup with manual marker information
      marker.bindPopup(`
        <div style="padding: 8px; min-width: 200px;">
          <h4 style="margin: 0 0 8px 0; color: #333;">📍 Manual Marker</h4>
          <p style="margin: 4px 0; color: #666;"><strong>Name:</strong> ${manualLocationName || 'Unnamed Location'}</p>
          <p style="margin: 4px 0; color: #666;"><strong>Coordinates:</strong> ${lat.toFixed(6)}, ${lng.toFixed(6)}</p>
          <p style="margin: 4px 0; color: #666;"><strong>Added:</strong> ${new Date().toLocaleString()}</p>
        </div>
      `);

      // Add to manual markers array (separate from job markers)
      setManualMarkers(prev => [...prev, marker]);
      
      // Center map on the new marker
      mapInstanceRef.current.setView([lat, lng], 13);
      
      // Clear the input fields
      setManualLat('');
      setManualLng('');
      setManualLocationName('');
      
      console.log('✅ Manual marker added successfully');
      
    } catch (error) {
      console.error('❌ Error adding manual marker:', error);
      alert('Error adding marker. Please try again.');
    } finally {
      setAddingManualMarker(false);
    }
  };

  // Function to delete all manual markers
  const deleteAllManualMarkers = () => {
    if (!mapInstanceRef.current) {
      console.error('❌ Map instance not available');
      return;
    }

    if (manualMarkers.length === 0) {
      console.log('ℹ️ No manual markers to delete');
      return;
    }

    console.log(`🗑️ Deleting ${manualMarkers.length} manual markers...`);

    // Remove all manual markers from the map
    manualMarkers.forEach(marker => {
      mapInstanceRef.current?.removeLayer(marker);
    });

    // Clear the manual markers array
    setManualMarkers([]);
    
    console.log('✅ All manual markers deleted successfully');
  };

  // Function to hide the radius circle and end radius mode
  const hideRadius = () => {
    if (!mapInstanceRef.current) {
      console.error('❌ Map instance not available');
      return;
    }

    console.log('🚫 Hiding radius circle and ending radius mode...');

    // Remove the radius circle from the map
    if (circleRef.current) {
      mapInstanceRef.current.removeLayer(circleRef.current);
      circleRef.current = null;
    }

    // Hide the radius UI
    console.log('🔄 Setting showRadius to false');
    setShowRadius(false);
    
    // Clear radius search results
    setJobsInRadius(null);
    
    console.log('✅ Radius hidden successfully');
  };

  // Function to update the radius circle
  const updateRadiusCircle = (lat: number, lng: number, customRadius?: number) => {
    if (!mapInstanceRef.current || !showRadius || addressMode) return;
    
    // Use custom radius if provided, otherwise use the current radiusKm state
    const radiusToUse = customRadius !== undefined ? customRadius : radiusKm;
    
    // Remove existing circle
    if (circleRef.current) {
      mapInstanceRef.current.removeLayer(circleRef.current);
    }
    
    // Create new circle with beautiful styling only if radius is visible and not in address mode
    if (showRadius && !addressMode) {
    const circle = L.circle([lat, lng], {
      radius: radiusToUse * 1000, // Convert km to meters
      color: '#2196f3', // Blue color
      fillColor: '#2196f3',
      fillOpacity: 0.15, // Semi-transparent
      weight: 3,
      opacity: 0.9,
      className: 'radius-circle', // Add class for custom styling
      dashArray: '5, 5' // Dashed border for a more elegant look
    }).addTo(mapInstanceRef.current);
    
    circleRef.current = circle;
    }
  };

  // Function to handle radius slider change
  const handleRadiusChange = (event: Event, newValue: number | number[]) => {
    const newRadius = newValue as number;
    setRadiusKm(newRadius);
    
    // Update circle if location is selected
    if (selectedLocation) {
      updateRadiusCircle(selectedLocation.lat, selectedLocation.lng);
    }
    
    // Reset job count when radius changes
    setJobsInRadius(null);
  };

  // Function to search for jobs within radius
  const handleFindJobsInRadius = async () => {
    if (!selectedLocation) return;
    
    setSearchingJobs(true);
    setJobsInRadius(null);
    
    try {
      // Use the real API to search for jobs
      const jobCount = await JobSearchService.getJobsCountInRadius({
        centerLat: selectedLocation.lat,
        centerLng: selectedLocation.lng,
        radiusKm: radiusKm
      });
      
      setJobsInRadius(jobCount);
      
      console.log('🔍 Jobs search completed:', {
        center: { lat: selectedLocation.lat, lng: selectedLocation.lng },
        radiusKm,
        jobCount,
        timestamp: new Date().toISOString()
      });
      
    } catch (error) {
      console.error('Error searching jobs in radius:', error);
      setJobsInRadius(0);
    } finally {
      setSearchingJobs(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    // Don't search if we have initial coordinates (we already have location data)
    if (initialCoordinates) {
      console.log('Skipping search - already have coordinates:', initialCoordinates);
      return;
    }

    try {
      setIsLoading(true);
      // Clear any previous errors
      setGeocodingFailed(false);
      setLocationError(null);
      
      console.log('🔍 Searching for:', searchQuery);
      
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1&addressdetails=1`
      );
      
      if (!response.ok) {
        throw new Error('Search service unavailable');
      }
      
      const data = await response.json();
      console.log('🔍 Search results:', data);
      
      if (data.length > 0) {
        const { lat, lon, display_name, address } = data[0];
        console.log('✅ Found location:', { lat, lon, display_name, address });
        
        const location: DetailedLocation = { 
          name: display_name, 
          lat: parseFloat(lat), 
          lng: parseFloat(lon),
          country: address?.country,
          state: address?.state,
          city: address?.city || address?.town || address?.village,
          district: address?.suburb || address?.neighbourhood,
          street: address?.road,
          houseNumber: address?.house_number,
          postalCode: address?.postcode,
        };
        
        setSelectedLocation(location);
        
        // Update markerAddress state so the Done button becomes enabled
        setMarkerAddress({
          coordinates: { lat: parseFloat(lat), lng: parseFloat(lon) },
          address: display_name,
          timestamp: new Date().toLocaleString(),
          country: address?.country || '',
          state: address?.state || '',
          city: address?.city || address?.town || address?.village || '',
          district: address?.suburb || address?.neighbourhood || '',
          street: address?.road || '',
          houseNumber: address?.house_number || '',
          postalCode: address?.postcode || ''
        });
        
        // Reset job count when location changes
        setJobsInRadius(null);
        
        // Show radius when location is selected (not in address mode)
        setShowRadius(!addressMode);
        
        // Update map view and marker
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setView([lat, lon], 13);
          
          if (markerRef.current) {
            markerRef.current.remove();
          }
          
          const marker = L.marker([lat, lon]).addTo(mapInstanceRef.current);
          markerRef.current = marker;
          
          // Add radius circle
          updateRadiusCircle(parseFloat(lat), parseFloat(lon));
        }
      } else {
        // No results found - show error overlay
        setGeocodingFailed(true);
        setLocationError(`Could not find location for: "${searchQuery}"`);
        console.log('❌ No search results found for:', searchQuery);
      }
    } catch (error) {
      console.error('Error searching location:', error);
      setGeocodingFailed(true);
      setLocationError(`Search failed for: "${searchQuery}". Please check your internet connection and try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetCurrentLocation = () => {
    if (navigator.geolocation) {
      setIsLoading(true);
      // Clear any previous errors
      setGeocodingFailed(false);
      setLocationError(null);
      
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          console.log('📍 GPS coordinates obtained:', { latitude, longitude });
          
          try {
            // Reverse geocode to get address
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
            );
            
            if (!response.ok) {
              throw new Error('Reverse geocoding service unavailable');
            }
            
            const data = await response.json();
            console.log('🔄 Reverse geocoding result:', data);
            
            if (data && data.display_name) {
              // Update search query with the found address
              setSearchQuery(data.display_name);
              
              // Create location object
              const address = data.address || {};
              const location: DetailedLocation = {
                name: data.display_name,
                lat: latitude,
                lng: longitude,
                country: address.country,
                state: address.state,
                city: address.city || address.town || address.village,
                district: address.suburb || address.neighbourhood,
                street: address.road,
                houseNumber: address.house_number,
                postalCode: address.postcode,
              };
              
              setSelectedLocation(location);
              
              // Update markerAddress state so the Done button becomes enabled
              setMarkerAddress({
                coordinates: { lat: latitude, lng: longitude },
                address: data.display_name,
                timestamp: new Date().toLocaleString(),
                country: address.country || '',
                state: address.state || '',
                city: address.city || address.town || address.village || '',
                district: address.suburb || address.neighbourhood || '',
                street: address.road || '',
                houseNumber: address.house_number || '',
                postalCode: address.postcode || ''
              });
              
              // Reset job count when location changes
              setJobsInRadius(null);
              
              // Show radius when location is selected (not in address mode)
              setShowRadius(!addressMode);
              
              // Update map view and marker
              if (mapInstanceRef.current) {
                mapInstanceRef.current.setView([latitude, longitude], 15);
                
                if (markerRef.current) {
                  markerRef.current.remove();
                }
                
                const marker = L.marker([latitude, longitude]).addTo(mapInstanceRef.current);
                markerRef.current = marker;
                
                // Add popup with location information
                marker.bindPopup(`
                  <div style="padding: 8px; min-width: 200px;">
                    <h4 style="margin: 0 0 8px 0; color: #333;">📍 Your Location</h4>
                    <p style="margin: 4px 0; color: #666;"><strong>Address:</strong> ${data.display_name}</p>
                    <p style="margin: 4px 0; color: #666;"><strong>Coordinates:</strong> ${latitude.toFixed(6)}, ${longitude.toFixed(6)}</p>
                  </div>
                `);
                
                // Open the popup
                marker.openPopup();
                
                // Add radius circle
                updateRadiusCircle(latitude, longitude);
              }
              
              console.log('✅ Current location set successfully');
            } else {
              throw new Error('No address found for current location');
            }
          } catch (error) {
            console.error('❌ Error reverse geocoding current location:', error);
            setGeocodingFailed(true);
            setLocationError(`Could not get address for your current location. Coordinates: ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
          } finally {
            setIsLoading(false);
          }
        },
        (error) => {
          console.error('❌ Error getting current location:', error);
          setIsLoading(false);
          
          // Show user-friendly error messages
          let errorMessage = 'Could not get your current location.';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied. Please allow location access and try again.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Your location is currently unavailable. Please try again.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please try again.';
              break;
          }
          
          setGeocodingFailed(true);
          setLocationError(errorMessage);
        }
      );
    } else {
      console.error('❌ Geolocation not supported');
      setGeocodingFailed(true);
      setLocationError('Geolocation is not supported by this browser.');
    }
  };

  const handleAddLocation = () => {
    if (selectedLocation) {
      onLocationSelect(selectedLocation);
      setSelectedLocation(null);
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
    }
  };

  const handleRemoveLocation = (locationToRemove: string) => {
    // This would need to be handled by the parent component
    // For now, we'll just show the selected locations
  };

  // Function to attempt geocoding of the address
  const attemptAddressGeocoding = async () => {
    if (!addressData) return;
    
    setGeocodingFailed(false);
    setLocationError(null);
    setIsLoading(true);
    
    try {
      // Construct the full address string
      const fullAddress = `${addressData.address_line_1}, ${addressData.postal_code} ${addressData.city}, ${addressData.country}`;
      console.log('Attempting to geocode address:', fullAddress);
      
      // Try to geocode the address
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}&limit=1&addressdetails=1`
      );
      
      if (!response.ok) {
        throw new Error('Geocoding service unavailable');
      }
      
      const data = await response.json();
      
      if (data.length === 0) {
        // No results found
        setGeocodingFailed(true);
        setLocationError(`Could not find location for: ${fullAddress}`);
        console.log('❌ No geocoding results found for address:', fullAddress);
        return;
      }
      
      const { lat, lon, display_name } = data[0];
      const latitude = parseFloat(lat);
      const longitude = parseFloat(lon);
      
      console.log('✅ Geocoding successful:', { latitude, longitude, display_name });
      
      // Center map on the found location
      if (mapInstanceRef.current) {
        mapInstanceRef.current.setView([latitude, longitude], 15);
        
        // Add a marker for the address
        if (markerRef.current) {
          markerRef.current.remove();
        }
        
        const marker = L.marker([latitude, longitude]).addTo(mapInstanceRef.current);
        markerRef.current = marker;
        
        // Add popup with address information
        marker.bindPopup(`
          <div style="padding: 8px; min-width: 200px;">
            <h4 style="margin: 0 0 8px 0; color: #333;">📍 ${addressData.title}</h4>
            <p style="margin: 4px 0; color: #666;"><strong>Address:</strong> ${fullAddress}</p>
            <p style="margin: 4px 0; color: #666;"><strong>Coordinates:</strong> ${latitude.toFixed(6)}, ${longitude.toFixed(6)}</p>
          </div>
        `);
        
        // Open the popup
        marker.openPopup();
      }
      
    } catch (error) {
      console.error('❌ Geocoding failed:', error);
      setGeocodingFailed(true);
      setLocationError(`Failed to locate address: ${addressData.address_line_1}, ${addressData.city}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle initial search query when modal opens
  useEffect(() => {
    if (open && initialSearchQuery && initialSearchQuery.trim()) {
      console.log('Setting initial search query:', initialSearchQuery);
      setSearchQuery(initialSearchQuery);
      
      // Trigger search after a short delay to ensure map is loaded
      // But only if we don't have initial coordinates (which means we already have location data)
      const searchTimeout = setTimeout(() => {
        if (mapLoaded && mapInstanceRef.current && !initialCoordinates && !addressMode) {
          console.log('Triggering initial search for:', initialSearchQuery);
          handleSearch();
        } else if (initialCoordinates) {
          console.log('Skipping search - using initial coordinates:', initialCoordinates);
          // Set the selected location from the initial coordinates
          const locationFromCoords = {
            name: initialSearchQuery || 'Selected Location',
            lat: initialCoordinates.lat,
            lng: initialCoordinates.lng,
            country: '',
            state: '',
            city: '',
            district: '',
            street: '',
            houseNumber: '',
            postalCode: ''
          };
          setSelectedLocation(locationFromCoords);
          
          // Update markerAddress state so the Done button becomes enabled
          setMarkerAddress({
            coordinates: { lat: initialCoordinates.lat, lng: initialCoordinates.lng },
            address: initialSearchQuery || 'Selected Location',
            timestamp: new Date().toLocaleString(),
            country: '',
            state: '',
            city: '',
            district: '',
            street: '',
            houseNumber: '',
            postalCode: ''
          });
          
          console.log('Set selected location from coordinates:', locationFromCoords);
        } else if (addressMode) {
          console.log('Skipping search - in address mode with existing data');
          // In address mode, we should still try to search if we don't have coordinates
          // This handles the case where the address data is passed but we need to geocode it
          if (!initialCoordinates) {
            console.log('Address mode without coordinates - attempting geocoding');
            handleSearch();
          }
        }
      }, 1000);
      
      return () => clearTimeout(searchTimeout);
    }
  }, [open, initialSearchQuery, mapLoaded, initialCoordinates, addressMode]);

  // Handle address mode geocoding when modal opens
  useEffect(() => {
    if (open && addressMode && addressData && mapLoaded && mapInstanceRef.current) {
      console.log('Attempting to geocode address in address mode:', addressData);
      
      // If we have coordinates from the map location, use them directly instead of searching
      if (initialCoordinates) {
        console.log('Using initial coordinates instead of geocoding:', initialCoordinates);
        const locationFromCoords = {
          name: addressData.full_name || 'Selected Location',
          lat: initialCoordinates.lat,
          lng: initialCoordinates.lng,
          country: addressData.country || '',
          state: addressData.state || '',
          city: addressData.city || '',
          district: '',
          street: addressData.address_line_1 || '',
          houseNumber: '',
          postalCode: addressData.postal_code || ''
        };
        setSelectedLocation(locationFromCoords);
        
        // Center the map on the coordinates
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setView([initialCoordinates.lat, initialCoordinates.lng], 15);
          
          // Add a marker at the coordinates
          if (markerRef.current) {
            markerRef.current.remove();
          }
          
          const marker = L.marker([initialCoordinates.lat, initialCoordinates.lng]).addTo(mapInstanceRef.current);
          markerRef.current = marker;
        }
        
        // Update markerAddress state so the Done button becomes enabled
        const addressString = `${addressData.address_line_1 || ''}, ${addressData.city || ''}, ${addressData.state || ''} ${addressData.postal_code || ''}, ${addressData.country || ''}`.replace(/,\s*,/g, ',').replace(/^,\s*|,\s*$/g, '');
        setMarkerAddress({
          coordinates: { lat: initialCoordinates.lat, lng: initialCoordinates.lng },
          address: addressString || 'Selected Location',
          timestamp: new Date().toLocaleString(),
          country: addressData.country || '',
          state: addressData.state || '',
          city: addressData.city || '',
          district: '',
          street: addressData.address_line_1 || '',
          houseNumber: '',
          postalCode: addressData.postal_code || ''
        });
        
        console.log('Set selected location from coordinates in address mode:', locationFromCoords);
        return;
      }
      attemptAddressGeocoding();
    }
  }, [open, addressMode, addressData, mapLoaded, initialCoordinates]);

  // Handle map resize when dialog opens or maximize state changes
  useEffect(() => {
    if (open && mapLoaded && mapInstanceRef.current) {
      console.log('🔄 Dialog opened or maximize state changed, resizing map...');
      const resizeTimeout = setTimeout(() => {
        if (mapInstanceRef.current) {
          console.log('🔄 Forcing map resize due to dialog state change...');
          mapInstanceRef.current.invalidateSize();
          // Trigger a small view change to force redraw
          const currentCenter = mapInstanceRef.current.getCenter();
          mapInstanceRef.current.setView(currentCenter, mapInstanceRef.current.getZoom());
        }
      }, 200);
      
      return () => clearTimeout(resizeTimeout);
    }
  }, [open, isMaximized, mapLoaded]);

  // Handle window resize events
  useEffect(() => {
    if (!open || !mapLoaded || !mapInstanceRef.current) return;

    const handleWindowResize = () => {
      console.log('🔄 Window resized, invalidating map size...');
      setTimeout(() => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.invalidateSize();
        }
      }, 100);
    };

    window.addEventListener('resize', handleWindowResize);
    
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [open, mapLoaded]);

  return (
    <>
      <style>
        {`
          .radius-circle {
            filter: drop-shadow(0 0 8px rgba(33, 150, 243, 0.3));
            animation: pulse 2s infinite;
          }
          
          .job-marker {
            animation: jobPulse 3s infinite;
          }
          
          .address-marker {
            animation: addressPulse 2.5s infinite;
          }
          
          /* Ensure map tiles render properly */
          .leaflet-container {
            width: 100% !important;
            height: 100% !important;
            background: #f0f0f0 !important;
          }
          
          .leaflet-tile-container {
            width: 100% !important;
            height: 100% !important;
          }
          
          .leaflet-tile {
            width: 256px !important;
            height: 256px !important;
          }
          
          @keyframes pulse {
            0% {
              filter: drop-shadow(0 0 8px rgba(33, 150, 243, 0.3));
            }
            50% {
              filter: drop-shadow(0 0 12px rgba(33, 150, 243, 0.5));
            }
            100% {
              filter: drop-shadow(0 0 8px rgba(33, 150, 243, 0.3));
            }
          }
          
          @keyframes jobPulse {
            0% {
              transform: scale(1);
              filter: drop-shadow(0 0 4px rgba(255, 107, 53, 0.4));
            }
            50% {
              transform: scale(1.1);
              filter: drop-shadow(0 0 8px rgba(255, 107, 53, 0.6));
            }
            100% {
              transform: scale(1);
              filter: drop-shadow(0 0 4px rgba(255, 107, 53, 0.4));
            }
          }
          
          @keyframes addressPulse {
            0% {
              transform: scale(1);
              filter: drop-shadow(0 0 4px rgba(244, 67, 54, 0.4));
            }
            50% {
              transform: scale(1.15);
              filter: drop-shadow(0 0 8px rgba(244, 67, 54, 0.6));
            }
            100% {
              transform: scale(1);
              filter: drop-shadow(0 0 4px rgba(244, 67, 54, 0.4));
            }
          }
          
          @keyframes neussPulse {
            0% {
              transform: rotate(-45deg) scale(1);
              box-shadow: 0 4px 12px rgba(255, 107, 53, 0.4), 0 0 0 4px rgba(255, 107, 53, 0.2);
            }
            50% {
              transform: rotate(-45deg) scale(1.1);
              box-shadow: 0 6px 16px rgba(255, 107, 53, 0.6), 0 0 0 6px rgba(255, 107, 53, 0.3);
            }
            100% {
              transform: rotate(-45deg) scale(1);
              box-shadow: 0 4px 12px rgba(255, 107, 53, 0.4), 0 0 0 4px rgba(255, 107, 53, 0.2);
            }
          }
          
          @keyframes orangeAddressPulse {
            0% {
              transform: rotate(-45deg) scale(1);
              box-shadow: 0 4px 12px rgba(255, 107, 53, 0.4), 0 0 0 4px rgba(255, 107, 53, 0.2);
            }
            50% {
              transform: rotate(-45deg) scale(1.05);
              box-shadow: 0 5px 14px rgba(255, 107, 53, 0.5), 0 0 0 5px rgba(255, 107, 53, 0.25);
            }
            100% {
              transform: rotate(-45deg) scale(1);
              box-shadow: 0 4px 12px rgba(255, 107, 53, 0.4), 0 0 0 4px rgba(255, 107, 53, 0.2);
            }
          }
        `}
      </style>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={isMaximized ? false : 'lg'}
        fullWidth={!isMaximized}
        fullScreen={isMaximized}
        PaperProps={{
          sx: {
            borderRadius: isMaximized ? 0 : 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            maxHeight: isMaximized ? '100vh' : '90vh',
            height: isMaximized ? '100vh' : '85vh',
          }
        }}
      >
      <DialogTitle sx={{ 
        pb: 1,
        borderBottom: '1px solid #e0e0e0',
        background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
        color: 'white',
        fontWeight: 'bold',
        fontSize: '1.25rem'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Place sx={{ fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            Search Locations on Map
          </Typography>
        </Box>
        <IconButton
          onClick={() => setIsMaximized(!isMaximized)}
          sx={{
            position: 'absolute',
            right: 48,
            top: 8,
            color: 'white',
            '&:hover': {
              background: 'rgba(255,255,255,0.1)',
            }
          }}
          aria-label={isMaximized ? 'Exit full screen' : 'Full screen'}
        >
          {isMaximized ? <FullscreenExit /> : <Fullscreen />}
        </IconButton>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'white',
            '&:hover': {
              background: 'rgba(255,255,255,0.1)',
            }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, height: isMaximized ? 'calc(100vh - 80px)' : 'calc(100% - 80px)', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
          {/* Map Container */}
          <Box sx={{ 
            flex: 1, 
            position: 'relative', 
            minHeight: '500px',
            width: '100%',
            height: '100%',
            overflow: 'hidden'
          }}>
            <div
              ref={mapRef}
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '0 0 0 8px',
                backgroundColor: '#f0f0f0',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              }}
            />
            {!mapLoaded && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255,255,255,0.95)',
                  borderRadius: '0 0 0 8px',
                  zIndex: 1000,
                }}
              >
                {mapError ? (
                  <>
                    <Typography variant="body1" color="error" sx={{ mb: 2, textAlign: 'center' }}>
                      {mapError}
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={retryMapInitialization}
                      sx={{
                        background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                        }
                      }}
                    >
                      Retry
                    </Button>
                  </>
                ) : (
                  <>
                    <CircularProgress sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      Loading map...
                    </Typography>
                  </>
                )}
              </Box>
            )}

            {/* Location Error Overlay */}
            {geocodingFailed && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255,255,255,0.95)',
                  borderRadius: '0 0 0 8px',
                  zIndex: 1001,
                }}
              >
                <Box sx={{
                  p: 4,
                  textAlign: 'center',
                  maxWidth: 400,
                  background: 'white',
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                  border: '2px solid #f44336',
                }}>
                  {/* Error Icon */}
                  <Box sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: '#ffebee',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px auto',
                    border: '3px solid #f44336',
                  }}>
                    <Typography sx={{ fontSize: 40, color: '#f44336' }}>
                      ⚠️
                    </Typography>
                  </Box>
                  
                  {/* Error Title */}
                  <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, color: '#d32f2f' }}>
                    {addressMode ? 'Location Not Found' : 'Search Failed'}
                  </Typography>
                  
                  {/* Error Message */}
                  <Typography variant="body1" sx={{ mb: 3, color: '#666', lineHeight: 1.6 }}>
                    {locationError}
                  </Typography>
                  
                  {/* Address Details */}
                  {addressData && (
                    <Box sx={{
                      p: 2,
                      backgroundColor: '#f5f5f5',
                      borderRadius: 2,
                      mb: 3,
                      textAlign: 'left',
                    }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#333' }}>
                        Address Details:
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Title:</strong> {addressData.title}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Name:</strong> {addressData.full_name}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Address:</strong> {addressData.address_line_1}
                      </Typography>
                      {addressData.address_line_2 && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          {addressData.address_line_2}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>City:</strong> {addressData.city}, {addressData.state}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Postal Code:</strong> {addressData.postal_code}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Country:</strong> {addressData.country}
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setGeocodingFailed(false);
                        setLocationError(null);
                        // Clear search query if it's a manual search error
                        if (!addressMode) {
                          setSearchQuery('');
                        }
                      }}
                      sx={{
                        borderColor: '#f44336',
                        color: '#f44336',
                        '&:hover': {
                          borderColor: '#d32f2f',
                          backgroundColor: 'rgba(244, 67, 54, 0.04)',
                        }
                      }}
                    >
                      Try Again
                    </Button>
                    <Button
                      variant="contained"
                      onClick={onClose}
                      sx={{
                        backgroundColor: '#f44336',
                        '&:hover': {
                          backgroundColor: '#d32f2f',
                        }
                      }}
                    >
                      Close
                    </Button>
                  </Box>
                  
                  {/* Help Text */}
                  <Typography variant="caption" sx={{ mt: 2, color: '#999', display: 'block' }}>
                    {addressMode 
                      ? 'This address may be incorrect, incomplete, or the location service may be unavailable.'
                      : 'Try searching with a different term, check your spelling, or the location service may be unavailable.'
                    }
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Side Panel */}
          <Box sx={{ 
            width: isMaximized ? 400 : 350, 
            background: '#f8f9fa',
            borderLeft: '1px solid #e0e0e0',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden'
          }}>
            <Box sx={{ 
              p: isMaximized ? 2 : 1.5, 
              pb: isMaximized ? 6 : 5, // Increased bottom padding to ensure Test section is fully visible
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              overflowY: 'auto',
              overflowX: 'hidden',
              flex: 1,
              minHeight: 0,
              maxHeight: '100%'
            }}>
            {/* Search Section */}
            <Paper sx={{ p: 1.5, background: 'white', borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 1.5, color: '#2196f3', fontSize: '1rem' }}>
                Search Location
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter city, address, or landmark..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={isLoading || !searchQuery.trim()}
                  sx={{
                    background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                    borderRadius: 2,
                    minWidth: 'auto',
                    px: 2,
                  }}
                >
                  <Search />
                </Button>
              </Box>

              <Button
                fullWidth
                variant="outlined"
                startIcon={<MyLocation />}
                onClick={handleGetCurrentLocation}
                disabled={isLoading}
                sx={{
                  borderColor: '#2196f3',
                  color: '#2196f3',
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: '#1976d2',
                    background: 'rgba(33, 150, 243, 0.04)',
                  }
                }}
              >
                Use My Location
              </Button>
            </Paper>

            {/* Job-related sections - only show when not in address mode and in job mode */}
            {!addressMode && isJobMode && (
              <>
            {/* Fetch Jobs Button */}
            <Paper sx={{ p: 1.5, background: 'white', borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 1.5, color: '#ff6b35', fontSize: '1rem' }}>
                💼 Job Statistics
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                Check how many jobs have coordinates available in the database
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                <Button
                  variant="contained"
                  onClick={fetchJobCount}
                  disabled={fetchingJobs}
                  startIcon={fetchingJobs ? <CircularProgress size={20} /> : <Search />}
                  sx={{
                    background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #f7931e 0%, #e65100 100%)',
                      boxShadow: '0 6px 16px rgba(255, 107, 53, 0.4)',
                      transform: 'translateY(-1px)',
                    },
                    '&:disabled': {
                      background: '#e0e0e0',
                      color: '#9e9e9e',
                      boxShadow: 'none',
                      transform: 'none',
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {fetchingJobs ? 'Fetching...' : 'Fetch Jobs'}
                </Button>
              </Box>
              
              {/* Job Count Results - Only show in job mode */}
              {isJobMode && jobCount !== null && (
                <Box sx={{ 
                  p: 2, 
                  background: jobCount > 0 
                    ? 'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)' 
                    : 'linear-gradient(135deg, #fff3e0 0%, #fce4ec 100%)',
                  borderRadius: 2,
                  border: `2px solid ${jobCount > 0 ? '#4caf50' : '#ff9800'}`,
                  textAlign: 'center'
                }}>
                  <Typography 
                    variant="h5" 
                    fontWeight="bold" 
                    sx={{ 
                      color: jobCount > 0 ? '#2e7d32' : '#f57c00',
                      mb: 1
                    }}
                  >
                    {jobCount > 0 ? '🎉' : '😔'} {jobCount} Job{jobCount !== 1 ? 's' : ''} Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {jobCount > 0 
                      ? `${jobCount} job${jobCount !== 1 ? 's' : ''} have coordinates available in the database`
                      : 'No jobs found with coordinates in the database'
                    }
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Selected Location */}
            {selectedLocation && (
              <Paper sx={{ p: 2, background: 'white', borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#2196f3' }}>
                  Selected Location
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Location Name:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {selectedLocation.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Coordinates: {selectedLocation.lat.toFixed(4)}, {selectedLocation.lng.toFixed(4)}
                  </Typography>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleAddLocation}
                  sx={{
                    background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                    borderRadius: 2,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                    }
                  }}
                >
                  Add This Location
                </Button>
              </Paper>
            )}

            {/* Detailed Location Information */}
            {selectedLocation && (
              <Paper sx={{ p: 2, background: 'white', borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#2196f3' }}>
                  📍 Detailed Address Information
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {selectedLocation.country && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, fontWeight: 'bold' }}>
                        🌍 Country:
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        {selectedLocation.country}
                      </Typography>
                    </Box>
                  )}
                  
                  {selectedLocation.state && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, fontWeight: 'bold' }}>
                        🏛️ State:
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        {selectedLocation.state}
                      </Typography>
                    </Box>
                  )}
                  
                  {selectedLocation.city && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, fontWeight: 'bold' }}>
                        🏙️ City:
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        {selectedLocation.city}
                      </Typography>
                    </Box>
                  )}
                  
                  {selectedLocation.district && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, fontWeight: 'bold' }}>
                        🏘️ District:
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        {selectedLocation.district}
                      </Typography>
                    </Box>
                  )}
                  
                  {(selectedLocation.street || selectedLocation.houseNumber) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, fontWeight: 'bold' }}>
                        🛣️ Street:
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        {selectedLocation.street} {selectedLocation.houseNumber}
                      </Typography>
                    </Box>
                  )}
                  
                  {selectedLocation.postalCode && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, fontWeight: 'bold' }}>
                        📮 Postal Code:
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        {selectedLocation.postalCode}
                      </Typography>
                    </Box>
                  )}
                  
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, fontWeight: 'bold' }}>
                      📐 Coordinates:
                    </Typography>
                    <Typography variant="body2" color="text.primary" sx={{ fontFamily: 'monospace' }}>
                      {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Radius Control */}
            {selectedLocation && showRadius && !addressMode && (
              <Paper sx={{ p: 2, background: 'white', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#2196f3' }}>
                  🔵 Search Radius
                </Typography>
                  <IconButton
                    onClick={hideRadius}
                    size="small"
                    sx={{
                      color: '#666',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        color: '#333'
                      }
                    }}
                  >
                    <Close />
                  </IconButton>
                </Box>
                
                <Box sx={{ px: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Adjust the search radius around your selected location
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 40 }}>
                      1 km
                    </Typography>
                    <Slider
                      value={radiusKm}
                      onChange={handleRadiusChange}
                      min={1}
                      max={50}
                      step={1}
                      sx={{
                        flex: 1,
                        color: '#2196f3',
                        '& .MuiSlider-thumb': {
                          backgroundColor: '#2196f3',
                          border: '2px solid white',
                          boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(33, 150, 243, 0.4)',
                          },
                        },
                        '& .MuiSlider-track': {
                          backgroundColor: '#2196f3',
                          border: 'none',
                        },
                        '& .MuiSlider-rail': {
                          backgroundColor: '#e0e0e0',
                        },
                      }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 50 }}>
                      50 km
                    </Typography>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
                    borderRadius: 2,
                    py: 1.5,
                    px: 2
                  }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ color: '#2196f3' }}>
                      {radiusKm} km radius
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                    The blue circle on the map shows your search area
                  </Typography>
                  
                  {/* Find Jobs Button */}
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      onClick={handleFindJobsInRadius}
                      disabled={searchingJobs}
                      startIcon={searchingJobs ? <CircularProgress size={20} /> : <Search />}
                      sx={{
                        background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                        color: 'white',
                        px: 4,
                        py: 1.5,
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                          boxShadow: '0 6px 16px rgba(33, 150, 243, 0.4)',
                          transform: 'translateY(-1px)',
                        },
                        '&:disabled': {
                          background: '#e0e0e0',
                          color: '#9e9e9e',
                          boxShadow: 'none',
                          transform: 'none',
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {searchingJobs ? 'Searching...' : 'Find Jobs in Radius'}
                    </Button>
                  </Box>
                  
                  {/* Job Count Results - Only show in job mode */}
                  {isJobMode && jobsInRadius !== null && (
                    <Box sx={{ 
                      mt: 2, 
                      p: 2, 
                      background: jobsInRadius > 0 
                        ? 'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)' 
                        : 'linear-gradient(135deg, #fff3e0 0%, #fce4ec 100%)',
                      borderRadius: 2,
                      border: `2px solid ${jobsInRadius > 0 ? '#4caf50' : '#ff9800'}`,
                      textAlign: 'center'
                    }}>
                      <Typography 
                        variant="h5" 
                        fontWeight="bold" 
                        sx={{ 
                          color: jobsInRadius > 0 ? '#2e7d32' : '#f57c00',
                          mb: 1
                        }}
                      >
                        {jobsInRadius > 0 ? '🎉' : '😔'} {jobsInRadius} Job{jobsInRadius !== 1 ? 's' : ''} Found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {jobsInRadius > 0 
                          ? `Found ${jobsInRadius} job${jobsInRadius !== 1 ? 's' : ''} within ${radiusKm} km of ${selectedLocation.city || selectedLocation.name}`
                          : `No jobs found within ${radiusKm} km of ${selectedLocation.city || selectedLocation.name}`
                        }
                      </Typography>
                      
                      {jobsInRadius > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{
                              borderColor: '#4caf50',
                              color: '#2e7d32',
                              '&:hover': {
                                borderColor: '#388e3c',
                                backgroundColor: 'rgba(76, 175, 80, 0.04)',
                              },
                            }}
                            onClick={() => {
                              // TODO: Navigate to jobs list with radius filter applied
                              console.log('Navigate to jobs list with radius filter');
                            }}
                          >
                            View Job Details
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Paper>
            )}

            {/* Jobs on Map - Only show in job mode */}
            {isJobMode && allJobs.length > 0 && (
              <Paper sx={{ p: 2, background: 'white', borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#ff6b35' }}>
                  💼 Jobs on Map ({allJobs.length})
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  All jobs with coordinates are displayed as orange markers on the map. Click on any marker to see job details.
                  <br />
                  <strong>🚀 One-Click Solution:</strong> Click on the orange job labels below to automatically add them as markers to the map!
                </Typography>

                {/* Debug: Show job data */}
                <Box sx={{ mb: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1, fontSize: '0.7rem' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}>
                    Debug - Job Data:
                  </Typography>
                  {allJobs.map((job, index) => (
                    <Typography key={job.id} variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                      Job {index + 1}: {job.name || 'No name'} | 
                      Location: {job.location_name || 'No location'} | 
                      Coords: {job.latitude ? job.latitude.toFixed(4) : 'null'}, {job.longitude ? job.longitude.toFixed(4) : 'null'}
                    </Typography>
                  ))}
                </Box>

                {/* Debug button to manually refresh markers */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    console.log('🔄 Manually refreshing job markers...');
                    console.log('🔄 Current allJobs:', allJobs);
                    console.log('🔄 Map instance available:', !!mapInstanceRef.current);
                    console.log('🔄 Map loaded:', mapLoaded);
                    displayJobMarkersOnMap(allJobs);
                  }}
                  sx={{
                    mb: 2,
                    borderColor: '#ff6b35',
                    color: '#ff6b35',
                    '&:hover': {
                      borderColor: '#e65100',
                      backgroundColor: 'rgba(255, 107, 53, 0.1)'
                    }
                  }}
                >
                  🔄 Refresh Markers ({allJobs.length})
                </Button>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {allJobs.slice(0, 5).map((job, index) => {
                    const hasCoordinates = job.latitude && job.longitude;
                    const coordinates = hasCoordinates 
                      ? `(${job.latitude.toFixed(4)}, ${job.longitude.toFixed(4)})`
                      : '(No coordinates)';
                    
                    return (
                    <Chip
                      key={job.id}
                        label={`🚀 ${job.location_name || 'Unknown Location'} ${coordinates}`}
                      size="small"
                        onClick={() => handleJobLabelClick(job)}
                      sx={{
                        background: '#fff3e0',
                        color: '#e65100',
                        border: '1px solid #ffb74d',
                          fontSize: '0.7rem',
                          maxWidth: '100%',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            background: '#ff6b35',
                            color: 'white',
                            transform: 'scale(1.05)',
                            boxShadow: '0 2px 8px rgba(255, 107, 53, 0.3)'
                          },
                          '& .MuiChip-label': {
                            whiteSpace: 'normal',
                            wordBreak: 'break-word'
                          }
                        }}
                      />
                    );
                  })}
                  {allJobs.length > 5 && (
                    <Chip
                      label={`+${allJobs.length - 5} more`}
                      size="small"
                      sx={{
                        background: '#f3e5f5',
                        color: '#7b1fa2',
                        border: '1px solid #ba68c8'
                      }}
                    />
                  )}
                </Box>
              </Paper>
            )}

            {/* Manual Marker Addition - Only show in job mode */}
            {isJobMode && (
              <Paper sx={{ p: 2, background: 'white', borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#ff6b35' }}>
                  📍 Add Manual Marker
                </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter coordinates manually to add a marker to the map. Useful for testing and adding specific locations.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Latitude"
                  placeholder="e.g., 51.198178"
                  value={manualLat}
                  onChange={(e) => setManualLat(e.target.value)}
                  size="small"
                  type="number"
                  inputProps={{ step: "0.000001" }}
                  helperText="Range: -90 to 90"
                />
                <TextField
                  fullWidth
                  label="Longitude"
                  placeholder="e.g., 6.691648"
                  value={manualLng}
                  onChange={(e) => setManualLng(e.target.value)}
                  size="small"
                  type="number"
                  inputProps={{ step: "0.000001" }}
                  helperText="Range: -180 to 180"
                />
              </Box>

              <TextField
                fullWidth
                label="Location Name (Optional)"
                placeholder="e.g., Neuss, Germany"
                value={manualLocationName}
                onChange={(e) => setManualLocationName(e.target.value)}
                size="small"
                sx={{ mb: 2 }}
              />

              <Button
                fullWidth
                variant="contained"
                onClick={addManualMarker}
                disabled={addingManualMarker || !manualLat || !manualLng}
                sx={{
                  backgroundColor: '#ff6b35',
                  '&:hover': {
                    backgroundColor: '#e65100'
                  },
                  '&:disabled': {
                    backgroundColor: '#cccccc'
                  },
                  mb: 1
                }}
              >
                {addingManualMarker ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                    Adding Marker...
                  </>
                ) : (
                  '📍 Add Marker to Map'
                )}
              </Button>

              {/* Delete all manual markers button */}
              <Button
                fullWidth
                variant="outlined"
                onClick={deleteAllManualMarkers}
                disabled={manualMarkers.length === 0}
                sx={{
                  borderColor: '#f44336',
                  color: '#f44336',
                  '&:hover': {
                    borderColor: '#d32f2f',
                    backgroundColor: 'rgba(244, 67, 54, 0.1)'
                  },
                  '&:disabled': {
                    borderColor: '#cccccc',
                    color: '#cccccc'
                  }
                }}
              >
                🗑️ Delete All Manual Markers ({manualMarkers.length})
              </Button>

              {/* Quick coordinate examples */}
              <Box sx={{ mt: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Quick examples:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    label="Neuss, Germany"
                    size="small"
                    onClick={() => {
                      setManualLat('51.198178');
                      setManualLng('6.691648');
                      setManualLocationName('Neuss, Germany');
                    }}
                    sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                  />
                  <Chip
                    label="New York, USA"
                    size="small"
                    onClick={() => {
                      setManualLat('40.7128');
                      setManualLng('-74.0060');
                      setManualLocationName('New York, USA');
                    }}
                    sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                  />
                  <Chip
                    label="London, UK"
                    size="small"
                    onClick={() => {
                      setManualLat('51.5074');
                      setManualLng('-0.1278');
                      setManualLocationName('London, UK');
                    }}
                    sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                  />
                </Box>
              </Box>
            </Paper>
            )}
              </>
            )}

            {/* Location Settings - Only show in shop mode */}
            {!isJobMode && selectedLocation && (
              <Paper sx={{ p: 2, background: 'white', borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#2196f3' }}>
                  🎯 Location Settings
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Adjust the search radius around your selected location to find products within your desired area.
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Search Radius
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ color: '#2196f3' }}>
                      {radiusKm} km
                    </Typography>
                  </Box>
                  
                  <Slider
                    value={radiusKm}
                    onChange={(_, value) => {
                      const newRadius = value as number;
                      setRadiusKm(newRadius);
                      // Update radius circle dynamically
                      if (selectedLocation) {
                        updateRadiusCircle(selectedLocation.lat, selectedLocation.lng, newRadius);
                      }
                    }}
                    min={1}
                    max={100}
                    step={1}
                    marks={[
                      { value: 1, label: '1km' },
                      { value: 25, label: '25km' },
                      { value: 50, label: '50km' },
                      { value: 75, label: '75km' },
                      { value: 100, label: '100km' }
                    ]}
                    sx={{
                      color: '#2196f3',
                      '& .MuiSlider-thumb': {
                        backgroundColor: '#2196f3',
                        border: '2px solid white',
                        boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(33, 150, 243, 0.4)',
                        },
                        '&.Mui-focusVisible': {
                          boxShadow: '0 4px 12px rgba(33, 150, 243, 0.4)',
                        }
                      },
                      '& .MuiSlider-track': {
                        background: 'linear-gradient(90deg, #2196f3 0%, #1976d2 100%)',
                        border: 'none',
                      },
                      '& .MuiSlider-rail': {
                        backgroundColor: '#e0e0e0',
                      },
                      '& .MuiSlider-mark': {
                        backgroundColor: '#2196f3',
                        opacity: 0.6,
                      },
                      '& .MuiSlider-markLabel': {
                        color: '#666',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                      }
                    }}
                  />
                </Box>

                {/* Manual Input Field */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Or enter radius manually:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      type="number"
                      value={radiusKm}
                      onChange={(e) => {
                        const value = Math.max(1, Math.min(100, Number(e.target.value) || 1));
                        setRadiusKm(value);
                      }}
                      inputProps={{ 
                        min: 1, 
                        max: 100,
                        style: { textAlign: 'center' }
                      }}
                      sx={{ 
                        width: '80px',
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#2196f3',
                          },
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#2196f3',
                          },
                        }
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      km
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        if (selectedLocation) {
                          updateRadiusCircle(selectedLocation.lat, selectedLocation.lng);
                        }
                      }}
                      sx={{
                        background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                        color: 'white',
                        px: 2,
                        py: 1,
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontWeight: 'bold',
                        fontSize: '0.875rem',
                        boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                          boxShadow: '0 4px 12px rgba(33, 150, 243, 0.4)',
                        }
                      }}
                    >
                      Apply
                    </Button>
                  </Box>
                </Box>

                {/* Address Search Section */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Search for addresses in database:
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={searchAddressesInRadius}
                    disabled={searchingAddresses}
                    startIcon={searchingAddresses ? <CircularProgress size={20} /> : <Search />}
                    sx={{
                      background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                      color: 'white',
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                        boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)',
                      },
                      '&:disabled': {
                        background: '#cccccc',
                        color: '#666',
                        boxShadow: 'none',
                      }
                    }}
                  >
                    {searchingAddresses ? 'Searching...' : '🔍 Search Addresses in Radius'}
                  </Button>
                </Box>

                {/* Address Search Results */}
                {addressSearchResults !== null && (
                  <Box sx={{ mb: 3 }}>
                    {/* Results Summary Header */}
                    <Box sx={{ 
                      p: 2, 
                      background: addressSearchResults > 0 
                        ? 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)' 
                        : 'linear-gradient(135deg, #fff3e0 0%, #fce4ec 100%)',
                      borderRadius: 2,
                      border: `2px solid ${addressSearchResults > 0 ? '#ff6b35' : '#ff9800'}`,
                      textAlign: 'center',
                      mb: 2
                    }}>
                      <Typography 
                        variant="h5" 
                        fontWeight="bold" 
                        sx={{ 
                          color: addressSearchResults > 0 ? '#ff6b35' : '#f57c00',
                          mb: 1
                        }}
                      >
                        {addressSearchResults > 0 ? '🧡' : '😔'} {addressSearchResults} Address{addressSearchResults !== 1 ? 'es' : ''} Found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {addressSearchResults > 0 
                          ? `Found ${addressSearchResults} address${addressSearchResults !== 1 ? 'es' : ''} with coordinates within ${radiusKm} km of your selected location`
                          : `No addresses found with coordinates within ${radiusKm} km of your selected location`
                        }
                      </Typography>
                      
                      {addressSearchResults > 0 && (
                        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              if (foundAddresses.length > 0) {
                                console.log('🏠 Showing addresses on map...');
                                displayAddressMarkersOnMap(foundAddresses);
                              }
                            }}
                            sx={{
                              borderColor: '#ff6b35',
                              color: '#ff6b35',
                              '&:hover': {
                                borderColor: '#e55a2b',
                                backgroundColor: 'rgba(255, 107, 53, 0.04)',
                              }
                            }}
                          >
                            📍 Show on Map
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              console.log('🔍 Debug info:', {
                                foundAddresses: foundAddresses.length,
                                addressMarkers: addressMarkers.length,
                                mapInstance: !!mapInstanceRef.current,
                                mapLoaded: mapLoaded,
                                selectedLocation: selectedLocation
                              });
                              if (foundAddresses.length > 0) {
                                console.log('🏠 Found addresses data:', foundAddresses);
                              }
                            }}
                            sx={{
                              borderColor: '#ff9800',
                              color: '#f57c00',
                              '&:hover': {
                                borderColor: '#f57c00',
                                backgroundColor: 'rgba(255, 152, 0, 0.04)',
                              }
                            }}
                          >
                            🔍 Debug
                          </Button>
                        </Box>
                      )}
                    </Box>

                    {/* Beautiful Address List */}
                    {addressSearchResults > 0 && foundAddresses.length > 0 && (
                      <Box sx={{
                        background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        overflow: 'hidden'
                      }}>
                        <Box sx={{
                          background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                          color: 'white',
                          p: 1.5,
                          textAlign: 'center'
                        }}>
                          <Typography variant="h6" fontWeight="bold" sx={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
                            🏠 Found Addresses
                          </Typography>
                        </Box>
                        
                        <Box sx={{ 
                          maxHeight: '400px', 
                          overflowY: 'auto',
                          '&::-webkit-scrollbar': {
                            width: '6px',
                          },
                          '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            background: '#ff6b35',
                            borderRadius: '3px',
                          },
                          '&::-webkit-scrollbar-thumb:hover': {
                            background: '#e55a2b',
                          }
                        }}>
                          {foundAddresses.map((address, index) => (
                            <Box
                              key={address.id}
                              sx={{
                                p: 2,
                                borderBottom: index < foundAddresses.length - 1 ? '1px solid #e0e0e0' : 'none',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 107, 53, 0.05)',
                                  transform: 'translateX(4px)',
                                }
                              }}
                              onClick={() => {
                                // Focus on this specific address on the map
                                if (mapInstanceRef.current && address.latitude && address.longitude) {
                                  mapInstanceRef.current.setView([address.latitude, address.longitude], 16);
                                  // Open the popup for this specific marker
                                  setTimeout(() => {
                                    const markers = addressMarkers;
                                    if (markers[index]) {
                                      markers[index].openPopup();
                                    }
                                  }, 500);
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                {/* Orange Number Badge */}
                                <Box sx={{
                                  background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                                  color: 'white',
                                  borderRadius: '50%',
                                  width: 32,
                                  height: 32,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 'bold',
                                  fontSize: '14px',
                                  flexShrink: 0,
                                  boxShadow: '0 2px 8px rgba(255, 107, 53, 0.3)'
                                }}>
                                  {index + 1}
                                </Box>
                                
                                {/* Address Details */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="subtitle2" fontWeight="bold" sx={{ 
                                    color: '#333',
                                    mb: 0.5,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    🏠 {address.address_line_1 || 'Address not available'}
                                  </Typography>
                                  
                                  <Typography variant="body2" color="text.secondary" sx={{ 
                                    mb: 0.5,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    🏙️ {address.city || 'City not available'}
                                  </Typography>
                                  
                                  <Typography variant="body2" color="text.secondary" sx={{ 
                                    mb: 0.5,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    🌍 {address.country || 'Country not available'}
                                  </Typography>
                                  
                                  {address.latitude && address.longitude && (
                                    <Typography variant="caption" color="text.secondary" sx={{ 
                                      fontFamily: 'monospace',
                                      backgroundColor: '#f5f5f5',
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                      display: 'inline-block'
                                    }}>
                                      📍 {address.latitude.toFixed(6)}, {address.longitude.toFixed(6)}
                                    </Typography>
                                  )}
                                </Box>
                                
                                {/* Click Indicator */}
                                <Box sx={{
                                  color: '#ff6b35',
                                  opacity: 0.6,
                                  transition: 'opacity 0.2s ease',
                                  '&:hover': {
                                    opacity: 1
                                  }
                                }}>
                                  <Typography variant="caption" sx={{ fontSize: '12px' }}>
                                    👆 Click to focus
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}>
                    💡 Quick Tips:
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    • 1-10km: Local neighborhood search
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    • 10-25km: City-wide search
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    • 25-50km: Regional search
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    • 50-100km: Wide area search
                  </Typography>
                </Box>
              </Paper>
            )}

            {/* Address Display Mode */}
            {addressMode && addressData && (
              <Paper sx={{ p: 2, background: 'white', borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#2196f3' }}>
                  📍 Address Information
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Search Query Display */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
                      Searched Address
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" sx={{ mb: 0.5 }}>
                      {searchQuery || initialSearchQuery || 'No address searched'}
                    </Typography>
                  </Box>

                  {/* Address Details */}
                  <Box sx={{
                    p: 2,
                    backgroundColor: 'grey.50',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.200',
                  }}>
                    {selectedLocation ? (
                      <>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          {selectedLocation.name}
                        </Typography>
                        {selectedLocation.street && selectedLocation.houseNumber && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {selectedLocation.street} {selectedLocation.houseNumber}
                          </Typography>
                        )}
                        {selectedLocation.city && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {selectedLocation.city}
                          </Typography>
                        )}
                        {selectedLocation.state && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {selectedLocation.state}
                          </Typography>
                        )}
                        {selectedLocation.country && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {selectedLocation.country}
                          </Typography>
                        )}
                        {selectedLocation.postalCode && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {selectedLocation.postalCode}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        {searchQuery ? 'Search for an address to see details here' : 'Enter an address in the search field above'}
                      </Typography>
                    )}
                  </Box>

                  {/* Action Button */}
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      if (selectedLocation) {
                        // Pass the selected location data
                        onLocationSelect(selectedLocation);
                      } else if (searchQuery) {
                        // Create a basic location object from search query
                        const basicLocation = {
                          name: searchQuery,
                          lat: 0,
                          lng: 0,
                          country: '',
                          state: '',
                          city: '',
                          district: '',
                          street: '',
                          houseNumber: '',
                          postalCode: ''
                        };
                        onLocationSelect(basicLocation);
                      }
                    }}
                    disabled={!selectedLocation && !searchQuery}
                    sx={{
                      background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                      borderRadius: 2,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                      },
                      '&:disabled': {
                        background: '#e0e0e0',
                        color: '#9e9e9e',
                      }
                    }}
                  >
                    Select This Address
                  </Button>
                </Box>
              </Paper>
            )}

            {/* Marker Location Tile */}
            <Paper sx={{ p: 2, background: 'white', borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#2196f3' }}>
                📍 Marker Location
              </Typography>
              
              {/* Marker Address Display */}
              {markerAddress.coordinates && (
                <Box sx={{
                  p: 2,
                  backgroundColor: 'grey.50',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  mb: 2,
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    📍 Blue Marker Address
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.4 }}>
                    {markerAddress.address}
                  </Typography>
                  
                  {/* Structured Address Components */}
                  {(markerAddress.street || markerAddress.city || markerAddress.state || markerAddress.country || markerAddress.postalCode) && (
                    <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 1 }}>
                        📋 Address Details:
                      </Typography>
                      {markerAddress.street && markerAddress.houseNumber && (
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                          🛣️ Street: {markerAddress.street} {markerAddress.houseNumber}
                        </Typography>
                      )}
                      {markerAddress.city && (
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                          🏙️ City: {markerAddress.city}
                        </Typography>
                      )}
                      {markerAddress.state && (
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                          🏛️ State: {markerAddress.state}
                        </Typography>
                      )}
                      {markerAddress.country && (
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                          🌍 Country: {markerAddress.country}
                        </Typography>
                      )}
                      {markerAddress.postalCode && (
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                          📮 Postal Code: {markerAddress.postalCode}
                        </Typography>
                      )}
                    </Box>
                  )}
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    Coordinates: {markerAddress.coordinates.lat.toFixed(6)}, {markerAddress.coordinates.lng.toFixed(6)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    Placed: {markerAddress.timestamp}
                  </Typography>
                </Box>
              )}

              {/* No Marker Message */}
              {!markerAddress.coordinates && (
                <Box sx={{
                  p: 2,
                  backgroundColor: 'grey.50',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  mb: 2,
                  textAlign: 'center',
                }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Click anywhere on the map to place a blue marker and see its address here!
                  </Typography>
                </Box>
              )}

              {/* Select Address Button */}
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  if (markerAddress.coordinates) {
                    // Create a location object from marker address with structured data
                    const markerLocation = {
                      name: markerAddress.address || 'Selected Location',
                      lat: markerAddress.coordinates.lat,
                      lng: markerAddress.coordinates.lng,
                      country: markerAddress.country || '',
                      state: markerAddress.state || '',
                      city: markerAddress.city || '',
                      district: markerAddress.district || '',
                      street: markerAddress.street || '',
                      houseNumber: markerAddress.houseNumber || '',
                      postalCode: markerAddress.postalCode || ''
                    };
                    onLocationSelect(markerLocation);
                  }
                }}
                disabled={!markerAddress.coordinates}
                sx={{
                  background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.5,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                  },
                  '&:disabled': {
                    background: '#e0e0e0',
                    color: '#9e9e9e',
                  }
                }}
              >
                Select This Address
              </Button>
            </Paper>

            {/* Selected Locations List */}
            {selectedLocations.length > 0 && (
              <Paper sx={{ p: 2, background: 'white', borderRadius: 2 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#2196f3' }}>
                  Selected Locations ({selectedLocations.length})
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedLocations.map((location, index) => (
                    <Chip
                      key={index}
                      label={location}
                      onDelete={() => handleRemoveLocation(location)}
                      sx={{
                        background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                        color: 'white',
                        border: 'none',
                        fontWeight: 'bold',
                        '& .MuiChip-deleteIcon': {
                          color: 'white',
                        }
                      }}
                    />
                  ))}
                </Box>
              </Paper>
            )}


            {/* Instructions */}
            <Paper sx={{ p: 2, background: 'white', borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, color: '#2196f3' }}>
                How to Use
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                • Click anywhere on the map to select a location
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                • Use the search box to find specific places
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                • Click "Use My Location" to get your current position
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                • Add multiple locations to your filter
              </Typography>
            </Paper>

            </Box>
            
            {/* Action Buttons - Fixed at bottom */}
            <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', background: '#f8f9fa' }}>
              {/* Status Message */}
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                {(markerAddress.coordinates || initialCoordinates) ? (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: 1,
                    p: 1.5,
                    backgroundColor: '#e8f5e8',
                    borderRadius: 2,
                    border: '2px solid #4caf50'
                  }}>
                    <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                      ✅ Address Found! You can now proceed.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: 1,
                    p: 1.5,
                    backgroundColor: '#fff3e0',
                    borderRadius: 2,
                    border: '2px solid #ff9800'
                  }}>
                    <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 'bold' }}>
                      ⚠️ Please select an address on the map first.
                    </Typography>
                  </Box>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  onClick={onClose}
                  variant="outlined"
                  fullWidth
                  sx={{
                    borderColor: '#e0e0e0',
                    color: 'text.secondary',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      borderColor: '#2196f3',
                      background: '#f8f9fa',
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    // Only allow closing if an address has been found
                    // Check both markerAddress.coordinates and initialCoordinates
                    const hasAddress = markerAddress.coordinates || initialCoordinates;
                    if (hasAddress) {
                      // Call onLocationSelect with the appropriate location data before closing
                      if (selectedLocation) {
                        // Use the selected location if available
                        onLocationSelect(selectedLocation);
                      } else if (markerAddress.coordinates) {
                        // Create location from marker address
                        const locationFromMarker = {
                          name: markerAddress.address || 'Selected Location',
                          lat: markerAddress.coordinates.lat,
                          lng: markerAddress.coordinates.lng,
                          country: markerAddress.country || '',
                          state: markerAddress.state || '',
                          city: markerAddress.city || '',
                          district: markerAddress.district || '',
                          street: markerAddress.street || '',
                          houseNumber: markerAddress.houseNumber || '',
                          postalCode: markerAddress.postalCode || ''
                        };
                        onLocationSelect(locationFromMarker);
                      } else if (initialCoordinates) {
                        // Create location from initial coordinates
                        const locationFromInitial = {
                          name: initialSearchQuery || 'Selected Location',
                          lat: initialCoordinates.lat,
                          lng: initialCoordinates.lng,
                          country: '',
                          state: '',
                          city: '',
                          district: '',
                          street: '',
                          houseNumber: '',
                          postalCode: ''
                        };
                        onLocationSelect(locationFromInitial);
                      }
                      onClose();
                    }
                  }}
                  variant="contained"
                  fullWidth
                  disabled={!markerAddress.coordinates && !initialCoordinates}
                  sx={{
                    background: (markerAddress.coordinates || initialCoordinates)
                      ? 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)'
                      : 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      background: (markerAddress.coordinates || initialCoordinates)
                        ? 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)'
                        : 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                    },
                    '&:disabled': {
                      background: '#e0e0e0',
                      color: '#9e9e9e',
                      cursor: 'not-allowed',
                    }
                  }}
                >
                  {(markerAddress.coordinates || initialCoordinates) ? '✅ Done' : '⚠️ Select Address First'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default MapSearchNew;
